import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  Dashboard,
  People,
  PersonAdd,
  Menu,
  Close,
  Logout,
  AdminPanelSettings,
  ExpandMore,
  ExpandLess,
  Support,
  Email,
  Campaign,
  Poll,
  Restaurant,
  FitnessCenter,
  Settings
} from '../../mui-icons';
import './AdminLayout.css';
import logoIcon from '../../assets/icon-no-name.webp';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, logout, getRoleName, canEdit, canManageAdmins } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Build menu items dynamically based on permissions
  const menuItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: <Dashboard />,
      exact: true
    },
    {
      path: '/admin/influencers',
      label: 'Influenciadores',
      icon: <PersonAdd />,
      // Inclui também cupons e comissões como páginas relacionadas
      relatedPaths: ['/admin/coupons', '/admin/commissions']
    },
    {
      path: '/admin/users',
      label: 'Usuários',
      icon: <People />,
      // Inclui também cadastros incompletos como página relacionada
      relatedPaths: ['/admin/users-temp']
    },
    {
      path: '/admin/support',
      label: 'Suporte',
      icon: <Support />
    },
    {
      path: '/admin/message-logs',
      label: 'Logs de Mensagens',
      icon: <Email />
    },
    {
      path: '/admin/notifications',
      label: 'Notificações',
      icon: <Campaign />
    },
    {
      path: '/admin/surveys',
      label: 'Pesquisas',
      icon: <Poll />
    },
    {
      path: '/admin/meals',
      label: 'Alimentos e Refeições',
      icon: <Restaurant />,
      // Inclui também ingredientes como página relacionada
      relatedPaths: ['/admin/ingredients']
    },
    {
      path: '/admin/activities',
      label: 'Atividades',
      icon: <FitnessCenter />
    },
    {
      path: '/admin/app-versions',
      label: 'Configuração',
      icon: <Settings />
    },
    // Only show for super_admin
    ...(canManageAdmins() ? [{
      path: '/admin/admins',
      label: 'Administradores',
      icon: <AdminPanelSettings />
    }] : [])
  ];

  const isActive = (path, exact = false, relatedPaths = []) => {
    if (exact) {
      return location.pathname === path;
    }
    // Verifica se a rota atual corresponde ao path ou a alguma rota relacionada
    if (location.pathname.startsWith(path)) {
      return true;
    }
    // Verifica rotas relacionadas
    for (const relatedPath of relatedPaths) {
      if (location.pathname.startsWith(relatedPath)) {
        return true;
      }
    }
    return false;
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="admin-layout surface-app">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="admin-overlay" onClick={closeMobileMenu} />
      )}

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="sidebar-logo" onClick={closeMobileMenu}>
            <img src={logoIcon} alt="PersonalNutri" className="sidebar-logo-img" />
            {sidebarOpen && <span className="sidebar-logo-text">Admin</span>}
          </Link>
          <button className="sidebar-toggle desktop-only" onClick={toggleSidebar}>
            {sidebarOpen ? <ExpandLess /> : <ExpandMore />}
          </button>
          <button className="sidebar-close mobile-only" onClick={closeMobileMenu}>
            <Close />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive(item.path, item.exact, item.relatedPaths || []) ? 'active' : ''}`}
              onClick={closeMobileMenu}
              title={!sidebarOpen ? item.label : undefined}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {sidebarOpen && <span className="sidebar-label">{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">
              <AdminPanelSettings />
            </div>
            {sidebarOpen && (
              <div className="admin-details">
                <span className="admin-name">{admin?.name || 'Admin'}</span>
                <span className="admin-role">{getRoleName()}</span>
              </div>
            )}
          </div>
          <button 
            className="logout-button" 
            onClick={handleLogout}
            title="Sair"
          >
            <Logout />
            {sidebarOpen && <span>Sair</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`admin-main ${sidebarOpen ? '' : 'expanded'}`}>
        {/* Header */}
        <header className="admin-header">
          <button className="mobile-menu-button" onClick={toggleMobileMenu}>
            <Menu />
          </button>
          
          <div className="header-title">
            <h1>{getPageTitle(location.pathname)}</h1>
          </div>

          <div className="header-actions">
            <div className="admin-badge">
              <AdminPanelSettings sx={{ fontSize: 20 }} />
              <span>{getRoleName()}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="admin-content surface-app">
          {children}
        </main>
      </div>
    </div>
  );
};

/**
 * Retorna o título da página baseado no path
 */
function getPageTitle(pathname) {
  const titles = {
    '/admin': 'Dashboard',
    '/admin/influencers': 'Influenciadores',
    '/admin/coupons': 'Cupons',
    '/admin/commissions': 'Comissões',
    '/admin/users': 'Usuários',
    '/admin/users-temp': 'Cadastros Incompletos',
    '/admin/support': 'Suporte',
    '/admin/message-logs': 'Logs de Mensagens',
    '/admin/notifications': 'Notificações por Email',
    '/admin/surveys': 'Pesquisas de Satisfação',
    '/admin/admins': 'Administradores',
    '/admin/meals': 'Alimentos e Refeições',
    '/admin/ingredients': 'Alimentos e Categorias',
    '/admin/activities': 'Atividades e Exercícios',
    '/admin/app-versions': 'Configuração'
  };

  // Verifica match exato primeiro
  if (titles[pathname]) {
    return titles[pathname];
  }

  // Verifica padrões especiais
  if (pathname.match(/\/admin\/users\/\d+\/meals/)) {
    return 'Refeições do Usuário';
  }

  // Verifica prefixos
  for (const [path, title] of Object.entries(titles)) {
    if (pathname.startsWith(path) && path !== '/admin') {
      return title;
    }
  }

  return 'Admin';
}

export default AdminLayout;

